<script setup lang="ts">
import Dialog from '~/components/molecules/Dialog.vue'
import {CAPTURE_REVIEW_DIALOG_DISPLAY_REQUESTED} from '~/types/bus-event-names'
import {useMediaVideoCapture} from '~/composables/studio/useMediaVideoCapture'
import {useDirectoryStore} from '~/stores/directory'
import {useUserStore} from '~/stores/user'
import type {DirectoryData} from '~/types/DirectoryData'
import MediaCardDirectories from '~/components/molecules/MediaCardDirectories.vue'
import {FieldMaxLength} from '~/types/FieldMaxLength'

const { uploadVideo, videoIsUploaded, lastUploadedCaptureData, submitVideo } = useMediaVideoCapture()

const dialog = ref<typeof Dialog|null>(null)
const formExpanded = ref(false)
const previewData = ref<PreviewData|null>(null)
const name = ref('')
const studentName = ref('')
const directoriesChecked = ref<DirectoryData[]|null>(null)
const mediaCardDirectories = ref<typeof MediaCardDirectories|null>(null)
const video = ref<HTMLVideoElement|null>(null)

const isMP4 = computed(() => {
  return previewData.value?.chosenMimeType && previewData.value?.chosenMimeType.indexOf('mp4') > -1
})
const allowStudentCapture =  computed(() => {
  return useDirectoryStore().directoryAllowsCapture && !useUserStore().isTeacher
})
const disableSubmit = computed(() => {
  return !videoIsUploaded.value || name.value.trim().length === 0 || (allowStudentCapture.value ? studentName.value.trim().length === 0 : false)
})

type PreviewData = {
  videoURL: string,
  chosenMimeType: string,
  width: number,
  height: number,
}

useBusEvent(CAPTURE_REVIEW_DIALOG_DISPLAY_REQUESTED, show)

function updateDirectoriesChecked(directories: DirectoryData[]) {
  directoriesChecked.value = directories.length ? directories : null
}

function show(data: PreviewData) {
  previewData.value = data
  formExpanded.value = false
  dialog.value?.show()
}

function onSaveClick() {
  formExpanded.value = true
  video.value?.pause()
  uploadVideo()
}

function close() {
  if (confirm(useNuxtApp().$i18n.t('_global.are_you_sure'))) {
    dialog.value?.close()
  }
}

function onSubmit() {
  if (disableSubmit.value) {
    return
  }
  submitVideo(name.value, studentName.value, directoriesChecked.value)
}

function resetFormData() {
  name.value = ''
  studentName.value = ''
  directoriesChecked.value = null
  mediaCardDirectories.value?.resetDirectories()
  video.value?.pause()
}
</script>

<template>
  <MoleculesDialog ref="dialog" @close="resetFormData">
    <div class="bg-white rounded-bento p-4 max-w-full transition-[width] duration-[500ms]" :class="[formExpanded ? 'w-[400px]' : 'w-[800px]']">
      <LayoutsSlideToggler :open="!formExpanded">
        <div class="relative mb-4" :style="{paddingTop: previewData ? (100 * previewData.height / previewData.width) + '%' : ''}">
          <video controls controlsList="nodownload" class="absolute top-0 left-0 w-full h-full" :src="isMP4 ? null : previewData?.videoURL" ref="video">
            <source v-if="isMP4" :type="previewData.chosenMimeType" :src="previewData.videoURL">
          </video>
        </div>
        <div class="flex gap-2">
          <AtomsButton :label="$t('_global.delete')" dark-color="red" class="flex-1" before-icon="thumb_down" @click="close" />
          <AtomsButton :label="$t('_global.save')" dark-color="green" class="flex-1" before-icon="thumb_up" @click="onSaveClick" />
        </div>
      </LayoutsSlideToggler>
      <LayoutsSlideToggler :open="formExpanded">
        <form @submit.prevent="onSubmit">
          <AtomsFormTextInput :label="$t('capture.name')" class="mb-2" :required="true" v-model="name" :max-length="useFieldMaxLength(FieldMaxLength.CAPTURE_TITLE)" />
          <template v-if="allowStudentCapture">
            <AtomsFormTextInput :label="$t('capture.student_name')" class="mb-2" :required="true" v-model="studentName" :max-length="useFieldMaxLength(FieldMaxLength.CAPTURE_STUDENT_NAME)" />
            <p class="flex my-4">
              <span class="material-icons-outlined mr-2 text-gold">warning</span>
              {{ $t('capture.shared_with_teacher') }}
            </p>
          </template>
          <template v-else>
            <MoleculesMediaCardDirectories ref="mediaCardDirectories" v-if="lastUploadedCaptureData" class="!p-0 mb-2" :media-data="lastUploadedCaptureData" context="dialog" output="local" @update:directories-checked="updateDirectoriesChecked"/>
          </template>
          <div class="flex justify-end gap-2 items-center">
            <span class="opacity-50" v-if="!videoIsUploaded">{{ $t('capture.uploading') }}</span>
            <AtomsButton :label="$t('_global.cancel')" hover-scheme="none" hover-text-color="red" @click="formExpanded = false" />
            <AtomsButton :label="$t('_global.save')" dark-color="domain" tag="button" :disabled="disableSubmit" />
          </div>
        </form>
      </LayoutsSlideToggler>
    </div>
  </MoleculesDialog>
</template>